import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { auth } from '../firebase';

const PrivateGallery = () => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    fetchUserImages();
  }, []);

  const fetchUserImages = async () => {
    try {
      const userId = auth.currentUser.uid;
      const token = await auth.currentUser.getIdToken();
      
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/user-images/${userId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data && response.data.images) {
        setImages(response.data.images);
      } else {
        console.warn("No images data received or invalid format:", response?.data);
        setImages([]);
      }
    } catch (error) {
      console.error('Error fetching user images:', error);
      setImages([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteImage = async (imageId) => {
    if (window.confirm('Are you sure you want to delete this image?')) {
      try {
        const token = await auth.currentUser.getIdToken();
        await axios.delete(`${process.env.REACT_APP_API_URL}/images/${imageId}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        setImages(images.filter(img => img.id !== imageId));
      } catch (error) {
        console.error('Error deleting image:', error);
      }
    }
  };

  const handleToggleVisibility = async (imageId, currentVisibility) => {
    try {
      const token = await auth.currentUser.getIdToken();
      const newVisibility = currentVisibility === 'public' ? 'private' : 'public';
      
      await axios.patch(
        `${process.env.REACT_APP_API_URL}/images/${imageId}/visibility`,
        { visibility: newVisibility },
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      setImages(images.map(img => 
        img.id === imageId 
          ? { ...img, visibility: newVisibility }
          : img
      ));
    } catch (error) {
      console.error('Error updating image visibility:', error);
    }
  };

  const styles = {
    container: {
      padding: '20px',
      maxWidth: '1200px',
      margin: '0 auto'
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(5, 1fr)', // 5 columns
      gap: '15px',
      marginTop: '20px'
    },
    card: {
      border: '1px solid #ddd',
      borderRadius: '8px',
      overflow: 'hidden',
      backgroundColor: 'white',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      transition: 'transform 0.2s',
      cursor: 'pointer'
    },
    imageContainer: {
      width: '100%',
      aspectRatio: '1',
      overflow: 'hidden'
    },
    image: {
      width: '100%',
      height: '100%',
      objectFit: 'cover'
    },
    imageInfo: {
      padding: '10px'
    },
    date: {
      color: '#666',
      fontSize: '0.8em',
      marginBottom: '5px'
    },
    prompt: {
      margin: '5px 0',
      fontSize: '0.9em',
      color: '#333'
    },
    buttonContainer: {
      display: 'flex',
      gap: '8px',
      marginTop: '8px'
    },
    visibilityButton: {
      padding: '4px 8px',
      borderRadius: '4px',
      border: 'none',
      cursor: 'pointer',
      fontSize: '0.8em',
      color: 'white'
    },
    deleteButton: {
      backgroundColor: '#dc3545',
      color: 'white',
      border: 'none',
      padding: '4px 8px',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '0.8em'
    },
    modal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    },
    modalContent: {
      backgroundColor: 'white',
      padding: '20px',
      borderRadius: '8px',
      maxWidth: '90%',
      maxHeight: '90vh',
      overflow: 'auto',
      position: 'relative'
    },
    closeButton: {
      position: 'absolute',
      right: '10px',
      top: '10px',
      backgroundColor: 'transparent',
      border: 'none',
      fontSize: '24px',
      cursor: 'pointer',
      color: '#333'
    },
    loading: {
      textAlign: 'center',
      padding: '20px',
      fontSize: '1.2rem',
      color: '#666'
    },
    promptCopy: {
      padding: '10px',
      backgroundColor: '#f8f9fa',
      borderRadius: '4px',
      marginTop: '10px',
      cursor: 'pointer',
      fontSize: '0.9em'
    }
  };

  if (loading) {
    return <div style={styles.loading}>Loading your studio...</div>;
  }

  return (
    <div style={styles.container}>
      <h2 style={{ fontSize: "24px", marginBottom: "20px", color: "#333" }}>
        Private Studio
      </h2>

      <div style={styles.grid}>
        {images.map((image, index) => (
          <div key={index} style={styles.card} onClick={() => setSelectedImage(image)}>
            <div style={styles.imageContainer}>
              <img
                src={image.image_url}
                alt={image.prompt}
                style={styles.image}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = 'https://via.placeholder.com/300?text=Image+Not+Found';
                }}
              />
            </div>
            <div style={styles.imageInfo}>
              <p style={styles.date}>
                {new Date(image.created_at).toLocaleDateString()}
              </p>
              <p style={styles.prompt}>{image.prompt}</p>
              <div style={styles.buttonContainer}>
                <button
                  style={{
                    ...styles.visibilityButton,
                    backgroundColor: image.visibility === 'public' ? "#4CAF50" : "#FFA500"
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleVisibility(image.id, image.visibility);
                  }}
                >
                  {image.visibility === 'public' ? 'Public' : 'Private'}
                </button>
                <button
                  style={styles.deleteButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteImage(image.id);
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {selectedImage && (
        <div style={styles.modal} onClick={() => setSelectedImage(null)}>
          <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
            <button
              style={styles.closeButton}
              onClick={() => setSelectedImage(null)}
            >
              ×
            </button>
            <img
              src={selectedImage.image_url}
              alt={selectedImage.prompt}
              style={{ maxWidth: '100%', maxHeight: '80vh' }}
            />
            
            <div style={{ marginTop: '20px' }}>
  <p><strong>Created:</strong> {new Date(selectedImage.created_at).toLocaleString()}</p>
  <div 
    style={styles.promptCopy} 
    onClick={(e) => {
      navigator.clipboard.writeText(selectedImage.prompt);
      e.target.innerHTML = 'Prompt copied!';
      setTimeout(() => {
        e.target.innerHTML = selectedImage.prompt;
      }, 1000);
    }}
  >
    {selectedImage.prompt}
  </div>
  <p><strong>Visibility:</strong> {selectedImage.visibility}</p>
  <div style={styles.buttonContainer}>
    <button
      style={{
        ...styles.visibilityButton,
        backgroundColor: selectedImage.visibility === 'public' ? "#4CAF50" : "#FFA500"
      }}
      onClick={() => handleToggleVisibility(selectedImage.id, selectedImage.visibility)}
    >
      Make {selectedImage.visibility === 'public' ? 'Private' : 'Public'}
    </button>
    <button
      style={styles.deleteButton}
      onClick={() => {
        handleDeleteImage(selectedImage.id);
        setSelectedImage(null);
      }}
    >
      Delete
    </button>


              </div>
            </div>
          </div>
        </div>
      )}

      {images.length === 0 && !loading && (
        <p style={{ textAlign: 'center', marginTop: '40px' }}>
          You haven't created any images yet. Head over to the Create page to get started!
        </p>
      )}
    </div>
  );
};

export default PrivateGallery;